import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, Bot, Target, UserCog, FileSignature } from "lucide-react";
import Logo from "./Logo";

const CALENDLY = "https://cal.com/rggsales/30min";

const NAV_LINKS = [
  { label: "What We Do", href: "/#pillars", match: "/#pillars" },
  { label: "How It Works", href: "/#how-it-works", match: "/#how-it-works" },
  { label: "Pricing", href: "/pricing", match: "/pricing" },
  // "Other Services" dropdown is injected between here and Contact
  { label: "Contact", href: "/contact", match: "/contact" },
];

export const SERVICES = [
  {
    label: "AI Agents & Automations",
    href: "/services/ai-agents",
    icon: Bot,
    desc: "Always-on AI agents and workflow automation that eliminate busywork and accelerate lead response.",
  },
  {
    label: "Lead Generation",
    href: "/services/lead-generation",
    icon: Target,
    desc: "Done-for-you outbound pipeline: targeting, multichannel outreach, and qualified meetings booked.",
  },
  {
    label: "Executive Assistants",
    href: "/services/executive-assistants",
    icon: UserCog,
    desc: "Dedicated executive assistants who take admin off your plate.",
  },
  {
    label: "RFP & Vendor Registration",
    href: "/services/rfp-vendor-registration",
    icon: FileSignature,
    desc: "Get registered, stay compliant, and win more public and enterprise contracts.",
  },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const closeTimer = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate("/");
    }
    requestAnimationFrame(() =>
      window.scrollTo({ top: 0, behavior: "smooth" })
    );
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setDropdownOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setDropdownOpen(true);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setDropdownOpen(false), 120);
  };

  const isActive = (link) => {
    if (link.match.startsWith("/#")) return false;
    return location.pathname === link.match;
  };
  const servicesActive = location.pathname.startsWith("/services");

  const renderLink = (l, onClick) => {
    const active = isActive(l);
    const className = `text-[14px] font-medium transition-colors ${
      active ? "text-[#16A06A]" : "text-slate-600 hover:text-[#16A06A]"
    }`;
    const testId = `nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`;
    if (l.href.startsWith("/#")) {
      return (
        <a key={l.label} href={l.href} onClick={onClick} data-testid={testId} className={className}>
          {l.label}
        </a>
      );
    }
    return (
      <Link
        key={l.label}
        to={l.href}
        onClick={onClick}
        data-testid={testId}
        className={className}
        aria-current={active ? "page" : undefined}
      >
        {l.label}
      </Link>
    );
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-[0_1px_0_rgba(15,23,42,0.06),0_8px_24px_rgba(15,23,42,0.05)]"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          <Link
            to="/"
            data-testid="nav-logo"
            onClick={handleLogoClick}
            className="flex items-center"
            aria-label="RevGen home"
          >
            <Logo />
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.slice(0, 3).map((l) => renderLink(l))}

            {/* Other Services dropdown */}
            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={handleEnter}
              onMouseLeave={handleLeave}
            >
              <button
                type="button"
                data-testid="nav-other-services-trigger"
                aria-haspopup="true"
                aria-expanded={dropdownOpen}
                onClick={() => setDropdownOpen((v) => !v)}
                className={`inline-flex items-center gap-1 text-[14px] font-medium transition-colors ${
                  servicesActive || dropdownOpen
                    ? "text-[#16A06A]"
                    : "text-slate-600 hover:text-[#16A06A]"
                }`}
              >
                Other Services
                <ChevronDown
                  size={14}
                  className={`transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
                />
              </button>

              {dropdownOpen && (
                <div
                  data-testid="nav-other-services-panel"
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-3"
                >
                  <div className="w-[340px] rounded-xl bg-white border border-slate-200 shadow-[0_12px_40px_rgba(15,23,42,0.10)] p-2">
                    {SERVICES.map((s) => {
                      const Icon = s.icon;
                      return (
                        <Link
                          key={s.href}
                          to={s.href}
                          data-testid={`nav-other-services-${s.href.split("/").pop()}`}
                          className="group flex items-start gap-3 rounded-lg px-3 py-3 hover:bg-[#E7F6EE] transition-colors"
                        >
                          <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#E7F6EE] text-[#16A06A] group-hover:bg-white group-hover:ring-1 group-hover:ring-[#16A06A]/30 transition-colors">
                            <Icon size={16} />
                          </span>
                          <span className="min-w-0">
                            <span className="block text-[14px] font-semibold text-[#0A1628] group-hover:text-[#0E7C53]">
                              {s.label}
                            </span>
                            <span className="block text-[12px] text-slate-500 mt-0.5 leading-snug">
                              {s.desc}
                            </span>
                          </span>
                        </Link>
                      );
                    })}
                    <Link
                      to="/services"
                      className="block text-center text-[12px] font-semibold text-[#16A06A] hover:text-[#0E7C53] mt-1 mb-1 py-2 border-t border-slate-100"
                    >
                      View all services →
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {NAV_LINKS.slice(3).map((l) => renderLink(l))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <a
              href={CALENDLY}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-book-call-btn"
              className="inline-flex items-center justify-center rounded-lg bg-[#16A06A] hover:bg-[#0E7C53] active:bg-[#0A6446] text-white text-[14px] font-semibold px-5 py-2.5 shadow-[0_1px_2px_rgba(15,23,42,0.08)] hover:shadow-[0_4px_14px_rgba(22,160,106,0.35)] transition-all"
            >
              Book a Free Assessment
            </a>
          </div>

          <button
            type="button"
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-lg border border-slate-200 text-[#0A1628] bg-white"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        {open && (
          <div
            data-testid="nav-mobile-panel"
            className="lg:hidden pb-6 pt-2 border-t border-slate-200 bg-white"
          >
            <div className="flex flex-col gap-4 pt-4">
              {NAV_LINKS.slice(0, 3).map((l) => renderLink(l, () => setOpen(false)))}

              <button
                type="button"
                data-testid="nav-mobile-services-toggle"
                onClick={() => setMobileServicesOpen((v) => !v)}
                className={`flex items-center justify-between text-[15px] font-medium ${
                  servicesActive ? "text-[#16A06A]" : "text-slate-700"
                }`}
              >
                Other Services
                <ChevronDown
                  size={16}
                  className={`transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`}
                />
              </button>
              {mobileServicesOpen && (
                <div className="pl-3 border-l-2 border-[#E7F6EE] flex flex-col gap-3">
                  {SERVICES.map((s) => (
                    <Link
                      key={s.href}
                      to={s.href}
                      onClick={() => setOpen(false)}
                      className="text-[14px] font-medium text-slate-600 hover:text-[#16A06A]"
                    >
                      {s.label}
                    </Link>
                  ))}
                  <Link
                    to="/services"
                    onClick={() => setOpen(false)}
                    className="text-[13px] font-semibold text-[#16A06A]"
                  >
                    View all services →
                  </Link>
                </div>
              )}

              {NAV_LINKS.slice(3).map((l) => renderLink(l, () => setOpen(false)))}

              <a
                href={CALENDLY}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center justify-center rounded-lg bg-[#16A06A] hover:bg-[#0E7C53] text-white text-[14px] font-semibold px-5 py-3"
              >
                Book a Free Assessment
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
